import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import API_BASE_URL from "../../config";
import "./AddDevice.css";

const EditDevice = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [popList, setPopList] = useState([]);
  const [credList, setCredList] = useState([]);
  const [profileList, setProfileList] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [modelList, setModelList] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [formData, setFormData] = useState({
    hostname: "",
    ip_address: "",
    device_type: "",
    technology: "",
    vendor_name: "",
    vendor_profile_id: "",
    credential_id: "",
    pop_id: "",
    serial_no: "",
    mac_address: "",
    status: 1
  });

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [deviceRes, popsRes, credsRes, profilesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/device-manage/get/${id}`),
          fetch(`${API_BASE_URL}/info-manage/pops`),
          fetch(`${API_BASE_URL}/info-manage/credentials`),
          fetch(`${API_BASE_URL}/device-manage/vendors`)
        ]);
        const device = await deviceRes.json();
        const pops = await popsRes.json();
        const creds = await credsRes.json();
        const profiles = await profilesRes.json();

        if (pops.success) setPopList(pops.data);
        if (creds.success) setCredList(creds.data);
        if (profiles.success) {
          setProfileList(profiles.data);
          if (device.success) {
            const d = device.data;
            const vendors = [...new Set(
              profiles.data
                .filter(p => p.device_type === d.device_type)
                .map(p => p.vendor_name)
            )];
            setVendorList(vendors);
            const models = profiles.data.filter(
              p => p.device_type === d.device_type && p.vendor_name === d.vendor_name
            );
            setModelList(models);
            const profile = profiles.data.find(p => p.id === parseInt(d.vendor_profile_id));
            setSelectedProfile(profile || null);
            setFormData({
              hostname: d.hostname || "",
              ip_address: d.ip_address || "",
              device_type: d.device_type || "",
              technology: d.technology || "",
              vendor_name: d.vendor_name || "",
              vendor_profile_id: d.vendor_profile_id || "",
              credential_id: d.credential_id || "",
              pop_id: d.pop_id || "",
              serial_no: d.serial_no || "",
              mac_address: d.mac_address || "",
              status: d.status ?? 1
            });
          }
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "device_type") {
      const vendors = [...new Set(
        profileList.filter(p => p.device_type === value).map(p => p.vendor_name)
      )];
      setVendorList(vendors);
      setModelList([]);
      setSelectedProfile(null);
      setFormData(prev => ({ ...prev, device_type: value, technology: "", vendor_name: "", vendor_profile_id: "" }));
      return;
    }

    if (name === "vendor_name") {
      const models = profileList.filter(
        p => p.device_type === formData.device_type && p.vendor_name === value
      );
      setModelList(models);
      setSelectedProfile(null);
      setFormData(prev => ({ ...prev, vendor_name: value, vendor_profile_id: "" }));
      return;
    }

    if (name === "vendor_profile_id") {
      const profile = profileList.find(p => p.id === parseInt(value));
      setSelectedProfile(profile || null);
      setFormData(prev => ({ ...prev, vendor_profile_id: value }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/device-manage/edit/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      const result = await res.json();
      if (result.success) {
        setShowModal(true);
      } else {
        alert(result.error || "Something went wrong");
      }
    } catch (err) { alert("Error connecting to server"); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div style={{ textAlign: "center", color: "#94a3b8", marginTop: "40px" }}>Loading...</div>
  );

  return (
    <div className="ad-container">
      <div className="ad-card">
        <h2 className="ad-title">{t("edit_device")}</h2>
        <form className="ad-form" onSubmit={handleSubmit}>

          <div className="ad-grid">
            <div className="ad-input-group">
              <label>Device Type</label>
              <select name="device_type" value={formData.device_type} onChange={handleChange} required>
                <option value="">Select Type</option>
                <option value="OLT">OLT</option>
                <option value="Router">Router</option>
                <option value="Switch">Switch</option>
                <option value="Other">Other</option>
              </select>
            </div>
            {formData.device_type === "OLT" && (
              <div className="ad-input-group">
                <label>Technology</label>
                <select name="technology" value={formData.technology} onChange={handleChange}>
                  <option value="">Select Technology</option>
                  <option value="EPON">EPON</option>
                  <option value="GPON">GPON</option>
                </select>
              </div>
            )}
          </div>

          <div className="ad-grid">
            <div className="ad-input-group">
              <label>Vendor</label>
              <select name="vendor_name" value={formData.vendor_name} onChange={handleChange} required>
                <option value="">Select Vendor</option>
                {vendorList.map((v, i) => (
                  <option key={i} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div className="ad-input-group">
              <label>Model</label>
              <select name="vendor_profile_id" value={formData.vendor_profile_id} onChange={handleChange} required>
                <option value="">Select Model</option>
                {modelList.map(m => (
                  <option key={m.id} value={m.id}>{m.model_name}</option>
                ))}
              </select>
            </div>
          </div>

          {selectedProfile && (
            <div className="ad-info-badge">
              Driver: <b>{selectedProfile.driver_file}</b> &nbsp;|&nbsp;
              Log: <b>{selectedProfile.log_method}</b> &nbsp;|&nbsp;
              Access: <b>{selectedProfile.access_method}</b>
            </div>
          )}

          <div className="ad-grid">
            <div className="ad-input-group">
              <label>Hostname</label>
              <input type="text" name="hostname" value={formData.hostname} onChange={handleChange} required />
            </div>
            <div className="ad-input-group">
              <label>IP Address</label>
              <input type="text" name="ip_address" value={formData.ip_address} onChange={handleChange} required />
            </div>
          </div>

          <div className="ad-grid">
            <div className="ad-input-group">
              <label>Serial No</label>
              <input type="text" name="serial_no" value={formData.serial_no} onChange={handleChange} />
            </div>
            <div className="ad-input-group">
              <label>MAC Address</label>
              <input type="text" name="mac_address" value={formData.mac_address} onChange={handleChange} />
            </div>
          </div>

          <div className="ad-grid">
            <div className="ad-input-group">
              <label>POP / Location</label>
              <select name="pop_id" value={formData.pop_id} onChange={handleChange}>
                <option value="">Select POP</option>
                {popList.map(p => (
                  <option key={p.id} value={p.id}>{p.pop_name}</option>
                ))}
              </select>
            </div>
            <div className="ad-input-group">
              <label>Credential</label>
              <select name="credential_id" value={formData.credential_id} onChange={handleChange}>
                <option value="">Select Credential</option>
                {credList.map(c => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="ad-input-group">
            <label>Status</label>
            <select name="status" value={formData.status} onChange={handleChange}>
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>

          <div style={{ display: "flex", gap: "12px" }}>
            <button type="button" className="ad-submit-btn" style={{ background: "#475569" }} onClick={() => navigate(-1)}>
              Back
            </button>
            <button type="submit" className="ad-submit-btn" disabled={saving}>
              {saving ? "Saving..." : t("update_device")}
            </button>
          </div>

        </form>
      </div>

      {showModal && (
        <div className="pop-modal-overlay">
          <div className="pop-modal-box">
            <h3>✅ Successful!</h3>
            <p>Device updated successfully.</p>
            <button onClick={() => navigate("/device-manage/device-list")} className="ad-submit-btn" style={{ width: "100%", marginTop: "10px" }}>
              Go to Device List
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditDevice;
